import { createContext, useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Shared hero-story controller. One `step` index drives the background image,
// the animated headline, and the progress bar together so words and visuals
// land in sync. The last step holds; meanwhile the background quietly keeps
// cycling `loopImages` so the composition never goes static.

const HeroStoryContext = createContext(null);
export const useHeroStory = () => useContext(HeroStoryContext);

// Detect narrow (phone) viewports so heroes can use gentler, subject-centered
// framing instead of the desktop transforms that push subjects off-screen.
export function useIsMobile(bp = 640) {
  const [m, setM] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp}px)`);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [bp]);
  return m;
}

// Framing style for a hero image: desktop uses its bespoke `tf`; mobile drops
// the transform entirely and crops to a per-image focus point (`posM`) so the
// person AND the wearable stay in frame on narrow phone screens.
export function frameStyle(img, isMobile) {
  return isMobile
    ? { transform: "none", objectPosition: img.posM || "50% 45%" }
    : { transform: img.tf || "none", objectPosition: img.pos || "center" };
}

// One large, equal-weight two-line beat. Sizes step down on phones so long
// platform lines never overflow the fixed headline area.
const BIG = "text-4xl sm:text-6xl md:text-7xl leading-[1.05]";
export function duo(a, b, size = BIG) {
  return (
    <>
      <span className={`block ${size}`}>{a}</span>
      <span className={`block ${size} mt-2 text-accent`}>{b}</span>
    </>
  );
}

export function HeroStoryProvider({ steps, loopImages = [], loopMs = 5000, paused = false, children }) {
  const [step, setStep] = useState(0);
  const [loopIdx, setLoopIdx] = useState(0);
  const last = steps.length - 1;
  const holding = step >= last;

  // Remaining time for the current beat (so hover-pause can resume in place).
  const remainRef = useRef(null);
  const startRef = useRef(0);

  // Reset the remaining time whenever the beat changes.
  useEffect(() => {
    remainRef.current = steps[step]?.dur ?? null;
  }, [step, steps]);

  // Advance through the beats; pause freezes the timer and keeps the remainder.
  useEffect(() => {
    if (holding || paused || remainRef.current == null) return;
    startRef.current = Date.now();
    const t = setTimeout(() => {
      remainRef.current = null;
      setStep((p) => Math.min(p + 1, last));
    }, remainRef.current);
    return () => {
      clearTimeout(t);
      const elapsed = Date.now() - startRef.current;
      remainRef.current = Math.max(0, (remainRef.current ?? 0) - elapsed);
    };
  }, [step, paused, holding, last]);

  // Once holding, keep the imagery moving softly under the final headline.
  useEffect(() => {
    if (!holding || paused || loopImages.length <= 1) return;
    const t = setInterval(
      () => setLoopIdx((p) => (p + 1) % loopImages.length),
      loopMs
    );
    return () => clearInterval(t);
  }, [holding, paused, loopImages.length, loopMs]);

  return (
    <HeroStoryContext.Provider
      value={{ steps, step, last, holding, loopIdx, loopImages, paused }}
    >
      {children}
    </HeroStoryContext.Provider>
  );
}

// Animated, blur-crossfading headline. Placement/height owned by the page.
export function HeroHeadline({ className = "", innerClassName = "" }) {
  const { steps, step } = useHeroStory();
  return (
    <div className={`relative ${className}`}>
      <AnimatePresence>
        <motion.h1
          key={step}
          initial={{ opacity: 0, y: 22, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -16, filter: "blur(10px)" }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className={`absolute inset-0 flex flex-col justify-center font-heading font-light tracking-tight ${innerClassName}`}
          data-testid="hero-animated-headline"
        >
          {steps[step].headline}
        </motion.h1>
      </AnimatePresence>
    </div>
  );
}

// Segmented progress bar, one segment per beat, the active one filling over
// that beat's duration so viewers sense the story moving.
export function HeroProgress({ className = "" }) {
  const { steps, step, holding, paused } = useHeroStory();
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="hero-progress">
      {steps.map((s, i) => {
        const done = holding || i < step;
        const active = !holding && i === step;
        return (
          <div
            key={i}
            className="h-[3px] flex-1 max-w-[64px] rounded-full bg-white/15 overflow-hidden"
            data-testid={`hero-progress-seg-${i}`}
          >
            <div
              key={active ? `active-${step}` : `static-${done}`}
              className="h-full bg-accent"
              style={
                active
                  ? {
                      animation: `heroFill ${s.dur}ms linear forwards`,
                      animationPlayState: paused ? "paused" : "running",
                    }
                  : { width: done ? "100%" : "0%", transition: "width 400ms ease" }
              }
            />
          </div>
        );
      })}
    </div>
  );
}

// Full-bleed crossfading image layer (no video). Used by image-only heroes.
export function HeroImages({ className = "" }) {
  const { steps, step, holding, loopIdx, loopImages } = useHeroStory();
  const isMobile = useIsMobile();
  const cur = steps[step].bg;
  const activeSrc = holding && loopImages.length ? loopImages[loopIdx].src : cur?.src;

  const map = new Map();
  steps.forEach((s) => s.bg && map.set(s.bg.src, s.bg));
  loopImages.forEach((im) => map.set(im.src, im));
  const imgs = [...map.values()];

  return (
    <div className={`absolute inset-0 ${className}`}>
      {imgs.map((img) => (
        <img
          key={img.src}
          src={img.src}
          alt={img.src === activeSrc ? img.alt : ""}
          aria-hidden={img.src !== activeSrc}
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out"
          style={{
            opacity: img.src === activeSrc ? 1 : 0,
            transitionDuration: "2000ms",
            transformOrigin: "center",
            ...frameStyle(img, isMobile),
          }}
        />
      ))}
    </div>
  );
}
